import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { returnSelectors } from 'store/reducers/returnSlice';
import { configSelectors } from 'store/reducers/configSlice';
import { ScannerAction, SCANNER_CONFIGS, parseScannerAction } from 'components/Admin/ScannerControl/scannerActions';
import ScannerPendingCard from 'components/Admin/ScannerControl/ScannerPendingCard';
import useReturnSlipActions from 'hooks/useReturnSlipActions';
import useGoHome from 'hooks/useGoHome';
import useGoBackNavigation from 'hooks/useGoBackNavigation';
import useBarcodeScanning from 'hooks/useBarcodeScanning';
import { setTargetInputMode } from 'store/reducers/navigationSlice';
import { scannerHandlers } from './scannerDispatch/handlers';
import type { AppView, ScannerContext, ScannerPageRefs, ScannerHandler, ExecuteDeps } from './scannerDispatch/types';

// ── Constants ─────────────────────────────────────────────────────────────────

const PENDING_TOAST_ID = 'scanner-pending-confirm';
const PENDING_TIMEOUT_MS = 15_000;

// ── Helpers ───────────────────────────────────────────────────────────────────

type ParsedScan = { type: 'ean'; ean: string } | { type: 'action'; action: ScannerAction } | { type: 'invalid' };

function parseRawScan(raw: string): ParsedScan {
    if (!raw.trimStart().startsWith('{')) return { type: 'ean', ean: raw };
    const action = parseScannerAction(raw);
    if (!action) return { type: 'invalid' };
    return { type: 'action', action };
}

function getAppView(pathname: string): AppView {
    if (/^\/returns\/[^/]+\/[^/]+/.test(pathname)) return 'openReturn';
    if (/^\/returns\/[^/]+/.test(pathname)) return 'returnStatusList';
    if (/^\/search\/order\//.test(pathname)) return 'openReturn';
    return 'home';
}

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useScannerDispatch — the single entry point for all scanner input.
 *
 * Orchestrates the scanner pipeline by delegating to focused sub-handlers:
 *   - handleEanScan: processes EAN barcodes
 *   - handleControlActionShortcuts: quick UI actions
 *   - handleDoubleConfirm: manages 1st/2nd/3rd scan flow
 */
const useScannerDispatch = (refs: ScannerPageRefs) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const goHome = useGoHome();
    const goBack = useGoBackNavigation();

    // Redux state
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const returnSlipId = useAppSelector(returnSelectors.selectReturnId);
    const returnSlipStatus = useAppSelector(returnSelectors.selectReturnSlipStatus);
    const buttonStates = useAppSelector(returnSelectors.selectReturnButtonStates);
    const hasSplitRows = useAppSelector(returnSelectors.selectHasSplitRows);
    const hasUnsavedChanges = useAppSelector(returnSelectors.selectHasUnsavedChanges);
    const reasonCodes = useAppSelector(configSelectors.selectReasonCodesMap);

    const { handleSaveWithStatus, handleCompleteReturnSlip } = useReturnSlipActions({
        returnSlip,
        returnSlipId,
        orderIdFromPath: undefined,
        returnStatus: returnSlipStatus ?? undefined,
    });

    // Double-confirm state
    const [pendingAction, setPendingAction] = useState<ScannerAction | null>(null);
    const [dialogConfirmPending, setDialogConfirmPending] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Cancel pending ────────────────────────────────────────────────────────

    const cancelPending = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        toast.dismiss(PENDING_TOAST_ID);
        setPendingAction(null);
        setDialogConfirmPending((wasOpen) => {
            if (wasOpen) refs.cancelDeleteDialogRef?.current?.();
            return false;
        });
        refs.cancelNavigationRef?.current?.();
    }, [refs]);

    const armTimeout = useCallback(
        (onExpire: () => void) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                onExpire();
                toast.dismiss(PENDING_TOAST_ID);
                toast.warning(t('scannerConfirmTimeout'), {
                    autoClose: 3000,
                    closeOnClick: true,
                    style: { minWidth: '22rem' },
                });
            }, PENDING_TIMEOUT_MS);
        },
        [t],
    );

    const showValidationError = useCallback(
        (errorKey: string) => {
            toast.error(t(errorKey), { autoClose: 3000, closeOnClick: true });
        },
        [t],
    );

    // ── Context & Dependencies builders ───────────────────────────────────────

    const buildScannerContext = useCallback((): ScannerContext => {
        return {
            view: getAppView(location.pathname),
            returnSlip: returnSlip ?? null,
            returnSlipStatus,
            returnSlipId: returnSlipId ?? null,
            buttonStates: buttonStates ?? null,
            hasSplitRows,
            hasUnsavedChanges,
            refs,
            pendingAction,
            dialogConfirmPending,
            reasonCodes,
        };
    }, [
        location.pathname,
        returnSlip,
        returnSlipStatus,
        returnSlipId,
        buttonStates,
        hasSplitRows,
        hasUnsavedChanges,
        refs,
        pendingAction,
        dialogConfirmPending,
        reasonCodes,
    ]);

    const buildExecuteDeps = useCallback((): ExecuteDeps => {
        return {
            handleSaveWithStatus,
            handleCompleteReturnSlip,
            goBack,
            goHome,
            navigateToHome: () => navigate('/'),
            setTargetInputMode: (mode: 'search' | 'barcode' | 'customer') => dispatch(setTargetInputMode(mode)),
            t,
        };
    }, [handleSaveWithStatus, handleCompleteReturnSlip, goBack, goHome, navigate, dispatch, t]);

    // ── EAN scanning handler ──────────────────────────────────────────────────

    const handleEanScan = useCallback(
        (ean: string, ctx: ScannerContext, deps: ExecuteDeps) => {
            const handler = scannerHandlers['eanScan'];
            const result = handler.validate(ctx, ean);
            if (!result.ok) {
                showValidationError(result.errorKey);
                return;
            }
            handler.execute(ctx, deps, ean);
        },
        [showValidationError],
    );

    // ── Control action shortcuts handler ──────────────────────────────────────

    const handleControlActionShortcuts = useCallback(
        (action: ScannerAction['action']) => {
            if (action === 'cancelAction') {
                refs.closeModalRef?.current?.();
                cancelPending();
                return true;
            }

            if (action === 'confirmNavigation') {
                refs.confirmNavDialogRef?.current?.();
                return true;
            }

            if ((action === 'goBackToOrderList' || action === 'newReturn') && refs.confirmNavDialogRef?.current) {
                refs.confirmNavDialogRef.current();
                return true;
            }

            if (action === 'deleteReturn' && dialogConfirmPending) {
                cancelPending();
                refs.confirmDeleteDialogRef.current?.();
                return true;
            }

            return false;
        },
        [refs, cancelPending, dialogConfirmPending],
    );

    // ── Double-confirm state machine ──────────────────────────────────────────

    const handleDoubleConfirm = useCallback(
        (
            scannerAction: ScannerAction,
            handler: ScannerHandler,
            ctx: ScannerContext,
            deps: ExecuteDeps,
            payload: any,
        ) => {
            if (!pendingAction) {
                // 1st scan — show pending toast and arm timeout
                setPendingAction(scannerAction);
                const config = SCANNER_CONFIGS.find((c) => c.action === scannerAction.action);
                if (config) {
                    toast(<ScannerPendingCard scannerConfig={config} />, {
                        toastId: PENDING_TOAST_ID,
                        style: { padding: '1rem', width: '35rem' },
                        autoClose: false,
                        closeOnClick: false,
                    });
                }
                armTimeout(() => setPendingAction(null));
            } else if (pendingAction.action === scannerAction.action) {
                // 2nd scan — same action, proceed
                cancelPending();
                if (handler.requiresDialogConfirm) {
                    refs.openDeleteDialogRef.current?.();
                    setDialogConfirmPending(true);
                    armTimeout(() => setDialogConfirmPending(false));
                } else {
                    handler.execute(ctx, deps, payload);
                }
            } else {
                // Different action scanned while waiting — reject
                toast.error(t('scannerWrongCode'), { autoClose: 3000, closeOnClick: true });
            }
        },
        [pendingAction, refs, armTimeout, cancelPending, t],
    );

    // ── Main scan handler (orchestrator) ──────────────────────────────────────

    const onRawScan = useCallback(
        (raw: string) => {
            const parsed = parseRawScan(raw);
            if (parsed.type === 'invalid') {
                showValidationError('scannerInvalidCode');
                return;
            }

            const ctx = buildScannerContext();
            const deps = buildExecuteDeps();

            // ── Route 1: EAN barcode ───────────────────────────────────────────
            if (parsed.type === 'ean') {
                handleEanScan(parsed.ean, ctx, deps);
                return;
            }

            // ── Route 2: Control action ────────────────────────────────────────
            const { action: scannerAction } = parsed;

            // Check for action shortcuts
            if (handleControlActionShortcuts(scannerAction.action)) {
                return;
            }

            // Look up standard handler
            const handler = scannerHandlers[scannerAction.action];
            if (!handler) return;

            // Extract payload based on action type
            const payload =
                scannerAction.action === 'bulkReason'
                    ? (scannerAction as { action: 'bulkReason'; value: number }).value
                    : scannerAction.action === 'openModal'
                    ? (scannerAction as {
                          action: 'openModal';
                          searchMode: 'orderNumber' | 'articleNumber' | 'barcode';
                      }).searchMode
                    : undefined;

            // Validate
            const validation = handler.validate(ctx, payload);
            if (!validation.ok) {
                showValidationError(validation.errorKey);
                return;
            }

            // Execute immediately if no double-confirm needed
            if (!handler.requiresDoubleConfirm) {
                handler.execute(ctx, deps, payload);
                return;
            }

            // Single-item pending — skip double-confirm and apply directly to that item
            if (scannerAction.action === 'bulkReason' && ctx.refs.pendingSingleItemRef.current) {
                handler.execute(ctx, deps, payload);
                return;
            }

            // Double-confirm flow
            handleDoubleConfirm(scannerAction, handler, ctx, deps, payload);
        },
        [
            showValidationError,
            buildScannerContext,
            buildExecuteDeps,
            handleEanScan,
            handleControlActionShortcuts,
            handleDoubleConfirm,
        ],
    );

    const { highlightedKey, clearHighlight } = useBarcodeScanning({
        onScanMatch: onRawScan,
        enabled: true,
    });

    return { highlightedKey, clearHighlight, cancelPending };
};

export default useScannerDispatch;
